import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import dayjs from "dayjs";
import UIWrapper from "../components/UIWrapper";
import { snackbarActions } from "../Store/snackbar";
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    CircularProgress,
    Stack,
    Alert,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Tab,
    Tabs,
} from "../mui";
import AdminService from "../services/AdminService";
import DownloadIcon from "@mui/icons-material/Download";

const metrics = [
    { label: "Projections", key: "projections", isBold: true },
    { label: "Number of Subjects rec'v (weekly)", key: "weekly_subject_codes_received", isBold: false },
    { label: "Number of Collection Kits rec'v (weekly)", key: "weekly_collection_kit_received", isBold: false },
    { label: "Number of Samples recv'd (weekly)", key: "weekly_tubes_received", isBold: false },
    { label: "Number of Subjects rec'v (cumulative)", key: "commulative_subject_codes_received", isBold: false },
    { label: "Number of Collection Kits rec'v (cumulative)", key: "commulative_collection_kit_received", isBold: false },
    { label: "Number of Samples recv'd (cumulative)", key: "commulative_tubes_received", isBold: false },
    { label: "# of Participants recv'd <30mL", key: "unusable_volumes", isBold: false },
];

const bloodDailyMetrics = [
    { label: "Alert 1: Low Volume", key: "unusable_volumes", isBold: false },
    { label: "Alert 2: # of Kits Expired", key: "kit_expiration_date_lessthen_date_received", isBold: false },
    { label: "Alert 3: # of samples with plasma grade >=3", key: "plasma_grade_greaterthen_3", isBold: false },
    { label: "Alert 4: # of samples where Collection To Freeze TAT > 168 hours", key: "collect_to_freeze_tat", isBold: false },
    { label: "Alert 5: # of samples where Sample Collect to Received > 3 Days", key: "collect_to_received", isBold: false },
    { label: "Alert 6: # of samples with Processing Deviation Codes", key: "sample_processing_deviation_codes", isBold: false },
];

const urineDailyMetrics = [
    { label: "Alert 1: # of subjects with unusable low volume (<30mL)", key: "unusable_low_volumes", isBold: false },
    { label: "Alert 2: # of subjects with unusable high volume (>106mL)", key: "unusable_high_volumes", isBold: false },
    { label: "Alert 3: # of Kits Expired", key: "kit_expiration_date_lessthen_date_received", isBold: false },
    { label: "Alert 4: Viability TAT (Collection to Freeze >7 days)", key: "collect_to_freeze_tat", isBold: false },
    { label: "Alert 5: Shipment to Sampled TAT (Collect to Receipt <4 days)", key: "collect_to_received", isBold: false },
    { label: "Alert 6: Processing TAT (Receipt to Freeze >3 days)", key: "processing_tat", isBold: false },
    { label: "Alert 7: Sample Processing Deviation Codes (< 30 ml volume)", key: "sample_processing_deviation_codes", isBold: false },
    { label: "Alert 8: Delay Adding Streck Buffer (>10 minutes)", key: "delay_adding_streck_buffer", isBold: false },
];

const tissueDailyMetrics = [
    { label: "Alert 1: Shipment to Sampled TAT (Collect to Receipt > 4 days)", key: "shipment_to_sampled_tat", isBold: false },
    { label: "Alert 2: Block Processing TAT (Receipt to Sectioning/Staining >10 days)", key: "block_processing_tat", isBold: false },
    { label: "Alert 3: Blocks TAT (Receipt to Shipped >30 days)", key: "blocks_tat", isBold: false },
    { label: "Alert 4: Internal Slides TAT (Receipt to Shipped >27 days)", key: "internal_slides_tat", isBold: false },
    { label: "Alert 5: External Slides TAT (Sectioning to Shipped >27 days)", key: "external_slides_tat", isBold: false },
];

const SAMPLE_TYPE_CONFIG = {
    blood: {
        label: "Blood Alerts",
        metrics: bloodDailyMetrics,
    },
    urine: {
        label: "Urine Alerts",
        metrics: urineDailyMetrics,
    },
    tissue: {
        label: "Tissue Alerts",
        metrics: tissueDailyMetrics,
    },
};

const getDailyMetricCellValue = (day, metricKey) => {
    if (!day) return "—";

    if (day[metricKey] !== undefined && day[metricKey] !== null) {
        return day[metricKey];
    }

    if (metricKey === "collect_to_freeze_tat" && day.viability_tat !== undefined && day.viability_tat !== null) {
        return day.viability_tat;
    }
    if (metricKey === "viability_tat" && day.collect_to_freeze_tat !== undefined && day.collect_to_freeze_tat !== null) {
        return day.collect_to_freeze_tat;
    }
    if (metricKey === "collect_to_received" && day.shipment_to_sampled_tat !== undefined && day.shipment_to_sampled_tat !== null) {
        return day.shipment_to_sampled_tat;
    }
    if (metricKey === "shipment_to_sampled_tat" && day.collect_to_received !== undefined && day.collect_to_received !== null) {
        return day.collect_to_received;
    }

    if (metricKey === "shipment_to_sampled_tat" || metricKey === "tissue_shipment_to_sampled_tat") {
        if (day.tissue_shipment_to_sampled_tat !== undefined && day.tissue_shipment_to_sampled_tat !== null) return day.tissue_shipment_to_sampled_tat;
        if (day.shipment_to_sampled_tat !== undefined && day.shipment_to_sampled_tat !== null) return day.shipment_to_sampled_tat;
        if (day.collect_to_received !== undefined && day.collect_to_received !== null) return day.collect_to_received;
    }

    if (metricKey === "block_processing_tat" || metricKey === "tissue_block_processing_tat") {
        if (day.tissue_block_processing_tat !== undefined && day.tissue_block_processing_tat !== null) return day.tissue_block_processing_tat;
        if (day.block_processing_tat !== undefined && day.block_processing_tat !== null) return day.block_processing_tat;
    }

    if (metricKey === "blocks_tat" || metricKey === "tissue_blocks_tat") {
        if (day.tissue_blocks_tat !== undefined && day.tissue_blocks_tat !== null) return day.tissue_blocks_tat;
        if (day.blocks_tat !== undefined && day.blocks_tat !== null) return day.blocks_tat;
    }

    if (metricKey === "internal_slides_tat" || metricKey === "tissue_internal_slides_tat") {
        if (day.tissue_internal_slides_tat !== undefined && day.tissue_internal_slides_tat !== null) return day.tissue_internal_slides_tat;
        if (day.internal_slides_tat !== undefined && day.internal_slides_tat !== null) return day.internal_slides_tat;
    }

    if (metricKey === "external_slides_tat" || metricKey === "tissue_external_slides_tat") {
        if (day.tissue_external_slides_tat !== undefined && day.tissue_external_slides_tat !== null) return day.tissue_external_slides_tat;
        if (day.external_slides_tat !== undefined && day.external_slides_tat !== null) return day.external_slides_tat;
    }

    if (metricKey === "kit_expiration_date_lessthen_date_received" && day.no_of_kits_expired !== undefined && day.no_of_kits_expired !== null) {
        return day.no_of_kits_expired;
    }
    if (metricKey === "no_of_kits_expired" && day.kit_expiration_date_lessthen_date_received !== undefined && day.kit_expiration_date_lessthen_date_received !== null) {
        return day.kit_expiration_date_lessthen_date_received;
    }

    return "—";
};

const getMetricsForSampleType = (sampleTypeKey, dataArray) => {
    let rawMetrics = [];
    if (SAMPLE_TYPE_CONFIG[sampleTypeKey] && SAMPLE_TYPE_CONFIG[sampleTypeKey].metrics.length > 0) {
        rawMetrics = SAMPLE_TYPE_CONFIG[sampleTypeKey].metrics;
    } else if (Array.isArray(dataArray) && dataArray.length > 0) {
        const keys = Object.keys(dataArray[0]).filter((k) => k !== "date");
        rawMetrics = keys.map((key, index) => ({
            label: `Alert ${index + 1}: ${formatColumnHeader(key)}`,
            key: key,
            isBold: false,
        }));
    }

    if (!Array.isArray(dataArray) || dataArray.length === 0) {
        return rawMetrics;
    }

    const filteredMetrics = rawMetrics.filter((metric) =>
        dataArray.some((item) => {
            const val = getDailyMetricCellValue(item, metric.key);
            return val !== "—" && val !== undefined && val !== null;
        })
    );

    return filteredMetrics.map((metric, index) => {
        let cleanLabel = metric.label || "";
        if (/^Alert\s+\d+:\s*/i.test(cleanLabel)) {
            cleanLabel = cleanLabel.replace(/^Alert\s+\d+:\s*/i, "");
        }
        return {
            ...metric,
            label: `Alert ${index + 1}: ${cleanLabel}`,
        };
    });
};

const alertDetailsConfig = {
    unusable_volumes: {
        type: "unusable-volume",
        title: "Unusable Volume Subject Codes",
        label: "Records",
    },
    unusable_low_volumes: {
        type: "unusable-low-volume",
        title: "Unusable Low Volume Subject Codes",
        label: "Records",
    },
    unusable_high_volumes: {
        type: "unusable-high-volume",
        title: "Unusable High Volume Subject Codes",
        label: "Records",
    },
    kit_expiration_date_lessthen_date_received: {
        type: "expired-before-received",
        title: "No. of Kits Expired",
        label: "Records",
    },
    no_of_kits_expired: {
        type: "expired-before-received",
        title: "No. of Kits Expired",
        label: "Records",
    },
    plasma_grade_greaterthen_3: {
        type: "plasma-grade",
        title: "Samples with Plasma Grade >= 3",
        label: "Records",
    },
    collect_to_freeze_tat: {
        type: "collect-to-freeze-tat",
        title: "Samples with Collect to Freeze TAT > 168 Hours",
        label: "Records",
    },
    collect_to_received: {
        type: "collect-to-received",
        title: "Samples with Collect to Received > 3 Days",
        label: "Records",
    },
    sample_processing_deviation_codes: {
        type: "sample-processing-deviation-codes",
        title: "Sample Processing Deviation Codes (< 30 ml volume)",
        label: "Records",
    },
    shipment_to_sampled_tat: {
        type: "shipment-to-sampled-tat",
        title: "Shipment to Sampled TAT (Collect to Receipt <4 days)",
        label: "Records",
    },
    processing_tat: {
        type: "processing-tat",
        title: "Processing TAT (Receipt to Freeze >3 days)",
        label: "Records",
    },
    viability_tat: {
        type: "viability-tat",
        title: "Viability TAT (Collection to Freeze >7 days)",
        label: "Records",
    },
    low_volume_alert: {
        type: "low-volume-alert",
        title: "Low Volume Alert @ >30",
        label: "Records",
    },
    high_volume_alert: {
        type: "high-volume-alert",
        title: "High Volume Alert @ <106",
        label: "Records",
    },
    delay_adding_streck_buffer: {
        type: "delay-adding-streck-buffer",
        title: "Delay Adding Streck Buffer (>10 minutes)",
        label: "Records",
    },
    tissue_shipment_to_sampled_tat: {
        type: "tissue-shipment-to-sampled-tat",
        title: "Shipment to Sampled TAT (Collect to Receipt > 4 days)",
        label: "Records",
    },
    block_processing_tat: {
        type: "block-processing-tat",
        title: "Block Processing TAT (Receipt to Sectioning/Staining >10 days)",
        label: "Records",
    },
    tissue_block_processing_tat: {
        type: "tissue-block-processing-tat",
        title: "Block Processing TAT (Receipt to Sectioning/Staining >10 days)",
        label: "Records",
    },
    blocks_tat: {
        type: "blocks-tat",
        title: "Blocks TAT (Receipt to Shipped >30 days)",
        label: "Records",
    },
    tissue_blocks_tat: {
        type: "tissue-blocks-tat",
        title: "Blocks TAT (Receipt to Shipped >30 days)",
        label: "Records",
    },
    internal_slides_tat: {
        type: "internal-slides-tat",
        title: "Internal Slides TAT (Receipt to Shipped >27 days)",
        label: "Records",
    },
    tissue_internal_slides_tat: {
        type: "tissue-internal-slides-tat",
        title: "Internal Slides TAT (Receipt to Shipped >27 days)",
        label: "Records",
    },
    external_slides_tat: {
        type: "external-slides-tat",
        title: "External Slides TAT (Sectioning to Shipped >27 days)",
        label: "Records",
    },
    tissue_external_slides_tat: {
        type: "tissue-external-slides-tat",
        title: "External Slides TAT (Sectioning to Shipped >27 days)",
        label: "Records",
    },
};

const COLUMN_LABEL_MAP = {
    subject_code: "Subject Code",
    collection_kit_label: "Kit ID",
    collection_kit_labels: "Kit IDs",
    collection_dates: "Collection Dates",
    volume_total: "Volume (mL)",
    volume: "Volume",
    kit_expiration_date: "Kit Expiry Date",
    date_received: "Date Received",
    inventory_code: "Inventory Code",
    plasma_grade: "Plasma Grade",
    draw_datetime: "Draw Date/Time",
    storage_dt: "Storage Date/Time",
    storage_datetime: "Storage Date/Time",
    received_datetime: "Received Date/Time",
    collection_date: "Collection Date",
    streck_datetime: "Streck Buffer Added Date/Time",
    hours_difference: "Time Calculation (Hours)",
    days_difference: "Days Difference",
    minutes_difference: "Time Calculation (Minutes)",
    service_name: "Service Name",
    sectioning_end_date: "Sectioning End Date",
    shipped_date: "Shipped Date",
    sectioning_date: "Sectioning Date",
};

const formatColumnHeader = (key) => {
    if (COLUMN_LABEL_MAP[key]) return COLUMN_LABEL_MAP[key];
    return key
        .replace(/_/g, " ")
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .replace(/\b\w/g, (char) => char.toUpperCase());
};

const getModalColumns = (data) => {
    if (!Array.isArray(data) || data.length === 0) return [{ id: "value", label: "Value" }];

    const allKeys = new Set();
    data.forEach((row) => {
        if (row && typeof row === "object") {
            Object.keys(row).forEach((k) => allKeys.add(k));
        }
    });

    if (allKeys.size === 0) {
        return [{ id: "value", label: "Value" }];
    }

    return Array.from(allKeys).map((key) => ({
        id: key,
        label: formatColumnHeader(key),
    }));
};

const formatTableCellValue = (val) => {
    if (val === null || val === undefined || val === "") return "—";

    if (Array.isArray(val)) {
        if (val.length === 0) return "—";
        return val.map((v) => formatTableCellValue(v)).join(", ");
    }

    if (typeof val === "object") {
        if (val.name || val.label || val.title || val.code || val.id) {
            return String(val.name || val.label || val.title || val.code || val.id);
        }
        try {
            return JSON.stringify(val);
        } catch (e) {
            return String(val);
        }
    }

    if (typeof val === "number") {
        if (!Number.isInteger(val)) {
            return val.toFixed(2);
        }
        return String(val);
    }

    if (typeof val === "string") {
        if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(val)) {
            return dayjs(val).format("YYYY-MM-DD HH:mm");
        }
        if (/^\d{4}-\d{2}-\d{2}$/.test(val)) {
            return dayjs(val).format("YYYY-MM-DD");
        }
    }

    return String(val);
};


const NateraDashboard = () => {
    const history = useHistory();
    const dispatch = useDispatch();

    const commonDispatch = (type, message) => {
        dispatch(
            snackbarActions.showNotification({
                snackbarOpen: true,
                snackbarType: type,
                snackbarMessage: message,
            })
        );
    };

    const [client, setClient] = useState("");
    const [projectsList, setProjectsList] = useState([]);
    const [selectedProject, setSelectedProject] = useState("");

    useEffect(() => {
        const userProfile = JSON.parse(sessionStorage.getItem("userProfile")) || {};
        if (userProfile.clients_projects) {
            const nateraKey = Object.keys(userProfile.clients_projects).find(
                (key) => key.toUpperCase() === "NATERA"
            );
            if (nateraKey) {
                setClient(nateraKey);
                const projects = userProfile.clients_projects[nateraKey];
                if (Array.isArray(projects) && projects.length > 0) {
                    setProjectsList(projects);
                    const defaultProject = projects.find(
                        (proj) => proj.toUpperCase() === "NATERA FIND-CRC"
                    ) || projects[0];
                    setSelectedProject(defaultProject);
                }
            }
        }
    }, []);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [tableData, setTableData] = useState([]);
    const [dailyLoading, setDailyLoading] = useState(true);
    const [dailyError, setDailyError] = useState(null);
    const [dailyDataMap, setDailyDataMap] = useState({});
    const [availableDailyTabs, setAvailableDailyTabs] = useState([]);
    const [activeDailyTab, setActiveDailyTab] = useState("blood");

    useEffect(() => {
        setDailyDataMap({});
        setAvailableDailyTabs([]);
        setActiveDailyTab("blood");
    }, [selectedProject]);

    const displayData = tableData && tableData.length > 0
        ? tableData
        : Array.from({ length: 10 }, (_, i) => ({
            week_date: null,
            projections: null,
            weekly_subject_codes_received: null,
            weekly_collection_kit_received: null,
            weekly_tubes_received: null,
            commulative_subject_codes_received: null,
            commulative_collection_kit_received: null,
            commulative_tubes_received: null,
            unusable_volumes: null,
        }));



    const [exporting, setExporting] = useState(false);
    const [detailsModalOpen, setDetailsModalOpen] = useState(false);
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [detailsError, setDetailsError] = useState(null);
    const [detailsData, setDetailsData] = useState([]);
    const [currentMetricKey, setCurrentMetricKey] = useState("");
    const [selectedDetailDate, setSelectedDetailDate] = useState(null);
    const [detailsTitle, setDetailsTitle] = useState("Details");
    const [detailsLabel, setDetailsLabel] = useState("Items");
    const [loadingCellKey, setLoadingCellKey] = useState(null);

    const getApiErrorMessage = (err) => {
        const responseData = err?.response?.data;

        if (typeof responseData === "string" && responseData.trim()) {
            return responseData.trim();
        }

        if (Array.isArray(responseData)) {
            return responseData.join(", ");
        }

        if (responseData && typeof responseData === "object") {
            if (typeof responseData.detail === "string" && responseData.detail.trim()) {
                return responseData.detail.trim();
            }
            if (typeof responseData.message === "string" && responseData.message.trim()) {
                return responseData.message.trim();
            }
            if (typeof responseData.error === "string" && responseData.error.trim()) {
                return responseData.error.trim();
            }
            try {
                return JSON.stringify(responseData);
            } catch (e) {
                return "Failed to load subject codes for the selected date.";
            }
        }

        if (typeof err?.message === "string" && err.message.trim()) {
            return err.message;
        }

        return "Failed to load subject codes for the selected date.";
    };

    const handleExport = async (interval, sourceType = "blood") => {
        if (!client || !selectedProject) return;
        setExporting(true);
        setError(null);
        try {
            const response = await AdminService.getCustomDashboardDataFile(client, selectedProject, interval, sourceType);
            const presignedUrl = response.data;
            if (presignedUrl) {
                const link = document.createElement("a");
                link.href = presignedUrl;
                const fileName = interval === "daily" && (sourceType === "urine" || sourceType === "tissue")
                    ? `${client}_${selectedProject}_custom_dashboard_${interval}_${sourceType}_data.csv`
                    : `${client}_${selectedProject}_custom_dashboard_${interval}_data.csv`;
                link.setAttribute("download", fileName);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                const sourceLabel = SAMPLE_TYPE_CONFIG[sourceType]?.label || `${formatColumnHeader(sourceType)} Alerts`;
                commonDispatch("success", `Successfully exported ${interval === "daily" ? sourceLabel : "custom dashboard weekly"} data file.`);
            } else {
                throw new Error("No presigned URL returned from server.");
            }
        } catch (err) {
            console.error(`Error exporting custom dashboard ${interval} ${sourceType} data file:`, err);
            const errMsg = err?.response?.data?.detail || `Failed to download custom dashboard ${interval} data file.`;
            setError(errMsg);
            commonDispatch("error", errMsg);
        } finally {
            setExporting(false);
        }
    };

    const openDetailsModal = async (date, metricKey) => {
        if (!date || !client || !selectedProject) return;

        const formattedDate = dayjs(date).format("YYYY-MM-DD");
        const detailConfig = alertDetailsConfig[metricKey];

        if (!detailConfig) return;

        const cellKey = `${formattedDate}_${metricKey}`;
        setLoadingCellKey(cellKey);
        setDetailsError(null);
        setDetailsData([]);
        setCurrentMetricKey(metricKey);
        setDetailsTitle(detailConfig.title);
        setDetailsLabel(detailConfig.label);
        setSelectedDetailDate(formattedDate);

        try {
            let response;
            let items = [];

            if (
                metricKey === "tissue_shipment_to_sampled_tat" ||
                (metricKey === "shipment_to_sampled_tat" && activeDailyTab === "tissue") ||
                (metricKey === "collect_to_received" && activeDailyTab === "tissue")
            ) {
                response = await AdminService.getTissueShipmentToSampledTat(client, selectedProject, formattedDate);
            } else if (metricKey === "block_processing_tat" || metricKey === "tissue_block_processing_tat") {
                response = await AdminService.getTissueBlockProcessingTat(client, selectedProject, formattedDate);
            } else if (metricKey === "blocks_tat" || metricKey === "tissue_blocks_tat") {
                response = await AdminService.getTissueBlocksTat(client, selectedProject, formattedDate);
            } else if (metricKey === "internal_slides_tat" || metricKey === "tissue_internal_slides_tat") {
                response = await AdminService.getTissueInternalSlidesTat(client, selectedProject, formattedDate);
            } else if (metricKey === "external_slides_tat" || metricKey === "tissue_external_slides_tat") {
                response = await AdminService.getTissueExternalSlidesTat(client, selectedProject, formattedDate);
            } else if (metricKey === "unusable_low_volumes") {
                response = await AdminService.getUrineLowVolumeSubjectCodes(client, selectedProject, formattedDate);
            } else if (metricKey === "unusable_high_volumes") {
                response = await AdminService.getUrineHighVolumeSubjectCodes(client, selectedProject, formattedDate);
            } else if (metricKey === "kit_expiration_date_lessthen_date_received" || metricKey === "no_of_kits_expired") {
                if (activeDailyTab === "urine") {
                    response = await AdminService.getUrineKitExpirationDateLessThanReceived(client, selectedProject, formattedDate);
                } else {
                    response = await AdminService.getSamplesExpiredBeforeReceived(client, selectedProject, formattedDate);
                }
            } else if (metricKey === "viability_tat" || (metricKey === "collect_to_freeze_tat" && activeDailyTab === "urine")) {
                response = await AdminService.getUrineViabilityTat(client, selectedProject, formattedDate);
            } else if (metricKey === "collect_to_freeze_tat") {
                response = await AdminService.getSamplesForCollectToFreezeTat(client, selectedProject, formattedDate);
            } else if (metricKey === "shipment_to_sampled_tat" || (metricKey === "collect_to_received" && activeDailyTab === "urine")) {
                response = await AdminService.getUrineShipmentToSampledTat(client, selectedProject, formattedDate);
            } else if (metricKey === "collect_to_received") {
                response = await AdminService.getSamplesForCollectToReceived(client, selectedProject, formattedDate);
            } else if (metricKey === "processing_tat") {
                response = await AdminService.getUrineProcessingTat(client, selectedProject, formattedDate);
            } else if (metricKey === "sample_processing_deviation_codes") {
                if (activeDailyTab === "urine") {
                    response = await AdminService.getUrineSampleProcessingDeviationCodes(client, selectedProject, formattedDate);
                } else {
                    response = await AdminService.getSampleProcessingDeviationCodes(client, selectedProject, formattedDate);
                }
            } else if (metricKey === "delay_adding_streck_buffer") {
                response = await AdminService.getUrineDelayAddingStreckBuffer(client, selectedProject, formattedDate);
            } else if (metricKey === "unusable_volumes") {
                response = await AdminService.getUnusableVolumeSubjectCodes(client, selectedProject, formattedDate);
            } else if (metricKey === "plasma_grade_greaterthen_3") {
                response = await AdminService.getSamplesForPlasmaGrade(client, selectedProject, formattedDate);
            }

            let resData = response?.data;
            if (typeof resData === "string") {
                try {
                    resData = JSON.parse(resData);
                } catch (err) {
                    console.warn("Failed to parse response data string:", err);
                }
            }

            if (Array.isArray(resData)) {
                items = resData;
            } else if (resData && typeof resData === "object") {
                const arrayKey = Object.keys(resData).find((k) => Array.isArray(resData[k]));
                if (arrayKey) {
                    items = resData[arrayKey];
                } else {
                    items = [resData];
                }
            }

            if (items.length === 0) {
                const labelLower = detailConfig.label.toLowerCase();
                const displayName = labelLower.includes("sample") ? "samples" : labelLower;
                const emptyMsg = `No ${displayName} found for the selected date.`;
                commonDispatch("error", emptyMsg);
            } else {
                setDetailsData(items);
                setDetailsModalOpen(true);
            }
        } catch (err) {
            console.error("Error fetching details:", err);
            const errMsg = getApiErrorMessage(err);
            setDetailsError(errMsg);
            commonDispatch("error", errMsg);
        } finally {
            setLoadingCellKey(null);
        }
    };

    const handleAlertCellClick = (day, metricKey) => {
        if (!day || !day.date || !client || !selectedProject) return;

        const detailConfig = alertDetailsConfig[metricKey];
        if (!detailConfig) return;

        const cellValue = getDailyMetricCellValue(day, metricKey);
        if (cellValue === "—" || Number(cellValue) === 0) {
            const labelLower = detailConfig.label.toLowerCase();
            const displayName = labelLower.includes("sample") ? "samples" : labelLower;
            const emptyMsg = `No ${displayName} found for the selected date.`;
            commonDispatch("error", emptyMsg);
            return;
        }

        openDetailsModal(day.date, metricKey);
    };

    const handleCloseDetailsModal = () => {
        setDetailsModalOpen(false);
    };

    const fetchDashboardData = async (clientVal, projectVal) => {
        setLoading(true);
        setDailyLoading(true);
        setError(null);
        setDailyError(null);

        let weeklyErrorMsg = null;
        let dailyErrorMsg = null;

        try {
            const response = await AdminService.getCustomDashboardData(clientVal, projectVal, "weekly");
            if (Array.isArray(response.data)) {
                if (response.data.length > 0) {
                    const sorted = [...response.data].sort((a, b) => {
                        return dayjs(b.week_end_date).valueOf() - dayjs(a.week_end_date).valueOf();
                    });

                    const mappedData = sorted.map(row => ({
                        ...row,
                        week_date: row.week_end_date
                    }));

                    setTableData(mappedData);
                } else {
                    setTableData([]);
                    weeklyErrorMsg = "No data available for this project.";
                    setError("No data available for this project.");
                }
            } else if (response.data && (response.data.detail || response.data.message || response.data.error)) {
                weeklyErrorMsg = response.data.detail || response.data.message || response.data.error;
                setError(weeklyErrorMsg);
                setTableData([]);
            } else if (typeof response.data === "string" && response.data.trim() !== "" && !response.data.trim().startsWith("<")) {
                weeklyErrorMsg = response.data;
                setError(weeklyErrorMsg);
                setTableData([]);
            } else {
                setTableData([]);
            }
        } catch (err) {
            console.error("Error fetching custom dashboard data:", err);
            weeklyErrorMsg = err?.response?.data?.detail || "Failed to load custom dashboard data.";
            setError(weeklyErrorMsg);
            setTableData([]);
        } finally {
            setLoading(false);
        }

        try {
            const response = await AdminService.getCustomDashboardData(clientVal, projectVal, "daily");
            const resData = response?.data;

            if (resData && typeof resData === "object" && !Array.isArray(resData)) {
                if (resData.detail || resData.message || resData.error) {
                    dailyErrorMsg = resData.detail || resData.message || resData.error;
                    setDailyError(dailyErrorMsg);
                    setDailyDataMap({});
                    setAvailableDailyTabs([]);
                } else {
                    const dataMap = {};
                    const availableTabs = [];

                    Object.keys(resData).forEach((key) => {
                        const arr = resData[key];
                        if (Array.isArray(arr) && arr.length > 0) {
                            const sorted = [...arr].sort((a, b) => dayjs(b.date).valueOf() - dayjs(a.date).valueOf());
                            dataMap[key] = sorted.slice(0, 10);
                            availableTabs.push(key);
                        } else if (Array.isArray(arr)) {
                            dataMap[key] = [];
                        }
                    });

                    setDailyDataMap(dataMap);
                    setAvailableDailyTabs(availableTabs);

                    if (availableTabs.length === 0) {
                        dailyErrorMsg = "No daily metrics data available for this project.";
                        setDailyError(dailyErrorMsg);
                    } else {
                        if (availableTabs.includes("blood")) {
                            setActiveDailyTab("blood");
                        } else {
                            setActiveDailyTab(availableTabs[0]);
                        }
                    }
                }
            } else if (Array.isArray(resData)) {
                if (resData.length > 0) {
                    const sorted = [...resData].sort((a, b) => dayjs(b.date).valueOf() - dayjs(a.date).valueOf());
                    const sliced = sorted.slice(0, 10);
                    setDailyDataMap({ blood: sliced });
                    setAvailableDailyTabs(["blood"]);
                    setActiveDailyTab("blood");
                } else {
                    setDailyDataMap({});
                    setAvailableDailyTabs([]);
                    dailyErrorMsg = "No daily metrics data available for this project.";
                    setDailyError(dailyErrorMsg);
                }
            } else if (typeof resData === "string" && resData.trim() !== "" && !resData.trim().startsWith("<")) {
                dailyErrorMsg = resData;
                setDailyError(dailyErrorMsg);
                setDailyDataMap({});
                setAvailableDailyTabs([]);
            } else {
                setDailyDataMap({});
                setAvailableDailyTabs([]);
            }
        } catch (err) {
            console.error("Error fetching custom daily dashboard data:", err);
            dailyErrorMsg = err?.response?.data?.detail || "Failed to load daily dashboard data.";
            setDailyError(dailyErrorMsg);
            setDailyDataMap({});
            setAvailableDailyTabs([]);
        } finally {
            setDailyLoading(false);
        }

        if (weeklyErrorMsg && dailyErrorMsg) {
            commonDispatch("error", `Weekly Metrics: ${weeklyErrorMsg}\nDaily Metrics: ${dailyErrorMsg}`);
        } else if (weeklyErrorMsg) {
            commonDispatch("error", `Weekly Metrics: ${weeklyErrorMsg}`);
        } else if (dailyErrorMsg) {
            commonDispatch("error", `Daily Metrics: ${dailyErrorMsg}`);
        }
    };

    useEffect(() => {
        if (!client || !selectedProject) {
            setLoading(false);
            setDailyLoading(false);
            return;
        }
        fetchDashboardData(client, selectedProject);
    }, [client, selectedProject]);

    const currentDailyData = dailyDataMap[activeDailyTab] || [];
    const currentDailyMetrics = getMetricsForSampleType(activeDailyTab, currentDailyData);

    return (
        <UIWrapper title="" paper={false}>
            {projectsList.length > 0 && (
                <Paper
                    elevation={1}
                    sx={{
                        p: 2,
                        mt: 1,
                        mb: 3,
                        borderRadius: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 2,
                        backgroundColor: "#ffffff",
                        border: "1px solid #eaeaea",
                    }}
                >
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel id="project-select-label">Project</InputLabel>
                        <Select
                            labelId="project-select-label"
                            id="project-select"
                            value={selectedProject}
                            label="Project"
                            onChange={(e) => setSelectedProject(e.target.value)}
                            disabled={loading}
                        >
                            {projectsList.map((proj) => (
                                <MenuItem key={proj} value={proj}>
                                    {proj}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Stack direction="row" spacing={1.5}>
                        <Button
                            variant="outlined"
                            color="secondary"
                            startIcon={exporting ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
                            onClick={() => handleExport("weekly", "blood")}
                            disabled={exporting || loading || !client || !selectedProject || !tableData || tableData.length === 0}
                            sx={{
                                textTransform: "none",
                                fontWeight: "bold",
                                borderRadius: 2,
                                borderColor: "#632584",
                                color: "#632584",
                                "&:hover": {
                                    borderColor: "#4a1964",
                                    backgroundColor: "rgba(99, 37, 132, 0.04)",
                                    transform: "translateY(-1px)",
                                    boxShadow: "0 4px 8px rgba(99, 37, 132, 0.1)",
                                },
                                transition: "all 0.2s ease-in-out",
                            }}
                        >
                            Weekly Export
                        </Button>
                    </Stack>
                </Paper>
            )}

            {(loading || (!error && tableData.length > 0)) && (
                <Paper elevation={3} sx={{ borderRadius: 2, overflow: "hidden" }}>
                    {loading ? (
                        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "250px" }}>
                            <CircularProgress color="secondary" />
                        </Box>
                    ) : (
                        <TableContainer sx={{ maxHeight: "calc(100vh - 160px)", overflowX: "auto" }}>
                            <Table stickyHeader size="small" aria-label="Natera FIND-CRC table">
                                <TableHead>
                                    <TableRow>
                                        <TableCell
                                            sx={{
                                                fontWeight: "bold",
                                                fontSize: "0.85rem",
                                                backgroundColor: "#f5f5f5",
                                                color: "#632584",
                                                position: "sticky",
                                                left: 0,
                                                zIndex: 3,
                                                borderRight: "2px solid #e0e0e0",
                                                minWidth: "210px",
                                                py: 1,
                                            }}
                                        >
                                            Week of
                                        </TableCell>
                                        {displayData.map((week, idx) => (
                                            <TableCell
                                                key={idx}
                                                align="center"
                                                sx={{
                                                    fontWeight: "bold",
                                                    fontSize: "0.82rem",
                                                    backgroundColor: idx === 0 ? "#ebfde7" : "#f5f5f5",
                                                    color: idx === 0 ? "#2e7d32" : "#333",
                                                    minWidth: "55px",
                                                    borderBottom: idx === 0 ? "3px solid #2e7d32" : "2px solid #632584",
                                                    py: 1,
                                                }}
                                            >
                                                {week.week_date ? dayjs(week.week_date).format("M/D/YY") : "—"}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {metrics.map((metric, rowIdx) => (
                                        <TableRow
                                            key={rowIdx}
                                            sx={{
                                                "&:hover": {
                                                    backgroundColor: "#fbf6ff",
                                                },
                                                transition: "background-color 0.15s ease",
                                            }}
                                        >
                                            <TableCell
                                                component="th"
                                                scope="row"
                                                sx={{
                                                    fontWeight: metric.isBold ? 600 : 500,
                                                    color: metric.isBold ? "#632584" : "#444",
                                                    fontSize: "0.8rem",
                                                    backgroundColor: "#fff",
                                                    position: "sticky",
                                                    left: 0,
                                                    zIndex: 2,
                                                    borderRight: "2px solid #e0e0e0",
                                                    boxShadow: "2px 0 5px -2px rgba(0,0,0,0.1)",
                                                    py: 0.75,
                                                }}
                                            >
                                                {metric.label}
                                            </TableCell>
                                            {displayData.map((week, colIdx) => (
                                                <TableCell
                                                    key={colIdx}
                                                    align="center"
                                                    sx={{
                                                        fontSize: "0.8rem",
                                                        fontWeight: (colIdx === 0 || metric.isBold) ? 600 : 400,
                                                        backgroundColor: colIdx === 0 ? "#f4fcf3" : "inherit",
                                                        color: colIdx === 0 ? "#2e7d32" : "#333",
                                                        py: 0.75,
                                                    }}
                                                >
                                                    {week[metric.key] !== undefined && week[metric.key] !== null ? week[metric.key] : "—"}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Paper>
            )}

            {(!dailyError && availableDailyTabs.length > 0) && (
                <Box sx={{ mt: 4 }}>
                    <Box sx={{ mb: 2, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
                        <Typography variant="h6" sx={{ fontWeight: "bold", color: "#632584" }}>
                            Daily Metrics
                        </Typography>

                        <Stack direction="row" spacing={1.5} alignItems="center">
                            <Button
                                variant="outlined"
                                color="secondary"
                                startIcon={exporting ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
                                onClick={() => handleExport("daily", activeDailyTab)}
                                disabled={exporting || loading || dailyLoading}
                                sx={{
                                    textTransform: "none",
                                    fontWeight: "bold",
                                    borderRadius: 2,
                                    borderColor: "#632584",
                                    color: "#632584",
                                    "&:hover": {
                                        borderColor: "#4a1964",
                                        backgroundColor: "rgba(99, 37, 132, 0.04)",
                                        transform: "translateY(-1px)",
                                        boxShadow: "0 4px 8px rgba(99, 37, 132, 0.1)",
                                    },
                                    transition: "all 0.2s ease-in-out",
                                }}
                            >
                                {`${SAMPLE_TYPE_CONFIG[activeDailyTab]?.label || formatColumnHeader(activeDailyTab)} Export`}
                            </Button>
                        </Stack>
                    </Box>

                    {availableDailyTabs.length > 0 && (
                        <Paper elevation={0} sx={{ mb: 2, borderBottom: 1, borderColor: "divider", backgroundColor: "transparent" }}>
                            <Tabs
                                value={availableDailyTabs.includes(activeDailyTab) ? activeDailyTab : availableDailyTabs[0]}
                                onChange={(e, newValue) => setActiveDailyTab(newValue)}
                                aria-label="daily metrics alert tabs"
                                textColor="secondary"
                                indicatorColor="secondary"
                                sx={{
                                    minHeight: "40px",
                                    "& .MuiTab-root": {
                                        textTransform: "none",
                                        fontWeight: 600,
                                        fontSize: "0.85rem",
                                        minHeight: "40px",
                                        py: 0.75,
                                        color: "#666",
                                        "&.Mui-selected": {
                                            color: "#632584",
                                            fontWeight: 700,
                                        },
                                    },
                                    "& .MuiTabs-indicator": {
                                        backgroundColor: "#632584",
                                        height: 3,
                                        borderRadius: "3px 3px 0 0",
                                    },
                                }}
                            >
                                {availableDailyTabs.map((tabKey) => {
                                    const label = SAMPLE_TYPE_CONFIG[tabKey]?.label || `${formatColumnHeader(tabKey)} Alerts`;
                                    return <Tab key={tabKey} label={label} value={tabKey} />;
                                })}
                            </Tabs>
                        </Paper>
                    )}

                    {dailyLoading && (
                        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "150px" }}>
                            <CircularProgress color="secondary" />
                        </Box>
                    )}

                    {!dailyLoading && !dailyError && (
                        currentDailyMetrics.length === 0 ? (
                            <Paper elevation={1} sx={{ p: 4, textAlign: "center", borderRadius: 2, backgroundColor: "#ffffff", border: "1px dashed #d0d0d0", mb: 2 }}>
                                <Typography variant="body1" sx={{ color: "#666", fontWeight: 500 }}>
                                    No alerts available for {SAMPLE_TYPE_CONFIG[activeDailyTab]?.label || formatColumnHeader(activeDailyTab)}.
                                </Typography>
                            </Paper>
                        ) : (
                            <Paper elevation={3} sx={{ borderRadius: 2, overflow: "hidden", mb: 2 }}>
                                <TableContainer sx={{ maxHeight: "350px", overflowX: "auto" }}>
                                    <Table stickyHeader size="small" aria-label="Natera Daily metrics table">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell
                                                    sx={{
                                                        fontWeight: "bold",
                                                        fontSize: "0.85rem",
                                                        backgroundColor: "#f5f5f5",
                                                        color: "#632584",
                                                        position: "sticky",
                                                        left: 0,
                                                        zIndex: 3,
                                                        borderRight: "2px solid #e0e0e0",
                                                        minWidth: "210px",
                                                        py: 1,
                                                    }}
                                                >
                                                    Day of
                                                </TableCell>
                                                {currentDailyData.map((day, idx) => (
                                                    <TableCell
                                                        key={idx}
                                                        align="center"
                                                        sx={{
                                                            fontWeight: "bold",
                                                            fontSize: "0.82rem",
                                                            backgroundColor: idx === 0 ? "#e3f2fd" : "#f5f5f5",
                                                            color: idx === 0 ? "#1565c0" : "#333",
                                                            minWidth: "55px",
                                                            borderBottom: idx === 0 ? "3px solid #1565c0" : "2px solid #632584",
                                                            py: 1,
                                                        }}
                                                    >
                                                        {day.date ? dayjs(day.date).format("M/D/YY") : "—"}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {currentDailyMetrics.map((metric, rowIdx) => (
                                                <TableRow
                                                    key={rowIdx}
                                                    sx={{
                                                        "&:hover": {
                                                            backgroundColor: "#fbf6ff",
                                                        },
                                                        transition: "background-color 0.15s ease",
                                                    }}
                                                >
                                                    <TableCell
                                                        component="th"
                                                        scope="row"
                                                        sx={{
                                                            fontWeight: metric.isBold ? 600 : 500,
                                                            color: metric.isBold ? "#632584" : "#444",
                                                            fontSize: "0.8rem",
                                                            backgroundColor: "#fff",
                                                            position: "sticky",
                                                            left: 0,
                                                            zIndex: 2,
                                                            borderRight: "2px solid #e0e0e0",
                                                            boxShadow: "2px 0 5px -2px rgba(0,0,0,0.1)",
                                                            py: 0.75,
                                                        }}
                                                    >
                                                        {metric.label}
                                                    </TableCell>
                                                    {currentDailyData.map((day, colIdx) => {
                                                        const formattedDate = day.date ? dayjs(day.date).format("YYYY-MM-DD") : null;
                                                        const cellKey = formattedDate ? `${formattedDate}_${metric.key}` : null;
                                                        const isCellLoading = loadingCellKey === cellKey;
                                                        const isClickable = day.date && alertDetailsConfig[metric.key] && !loadingCellKey;

                                                        return (
                                                            <TableCell
                                                                key={colIdx}
                                                                align="center"
                                                                sx={{
                                                                    fontSize: "0.8rem",
                                                                    fontWeight: (colIdx === 0 || metric.isBold) ? 600 : 400,
                                                                    backgroundColor: colIdx === 0 ? "#f0f7ff" : "inherit",
                                                                    color: colIdx === 0 ? "#1565c0" : "#333",
                                                                    py: 0.75,
                                                                    cursor: isClickable ? "pointer" : "default",
                                                                    textDecoration: isClickable ? "underline" : "none",
                                                                    textDecorationColor: isClickable ? "#1565c0" : "inherit",
                                                                    textDecorationThickness: isClickable ? "1px" : "0",
                                                                    ...(isClickable ? {
                                                                        '&:hover': {
                                                                            backgroundColor: "#e8f1ff",
                                                                        },
                                                                    } : {}),
                                                                }}
                                                                onClick={() => {
                                                                    if (isClickable) {
                                                                        handleAlertCellClick(day, metric.key);
                                                                    }
                                                                }}
                                                            >
                                                                {isCellLoading ? (
                                                                    <CircularProgress size={16} color="secondary" />
                                                                ) : (
                                                                    getDailyMetricCellValue(day, metric.key)
                                                                )}
                                                            </TableCell>
                                                        );
                                                    })}
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Paper>
                        )
                    )}
                </Box>
            )}

            <Dialog open={detailsModalOpen} onClose={handleCloseDetailsModal} maxWidth="md" fullWidth>
                <DialogTitle sx={{ fontWeight: 600, color: "#632584", pb: 1 }}>
                    {detailsTitle}
                </DialogTitle>
                <DialogContent dividers sx={{ p: 2 }}>
                    <Box sx={{ mb: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Typography variant="body2" color="text.secondary">
                            Client: <strong>{client || "—"}</strong> • Project: <strong>{selectedProject || "—"}</strong> • Date: <strong>{selectedDetailDate || "—"}</strong>
                        </Typography>
                        {detailsData.length > 0 && (
                            <Typography variant="caption" sx={{ fontWeight: 600, color: "#632584" }}>
                                Total Records: {detailsData.length}
                            </Typography>
                        )}
                    </Box>

                    {detailsLoading ? (
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", py: 4, gap: 1.5 }}>
                            <CircularProgress size={32} color="secondary" />
                            <Typography variant="body2" color="text.secondary">
                                Loading details, please wait...
                            </Typography>
                        </Box>
                    ) : null}

                    {!detailsLoading && detailsError ? (
                        <Alert severity="error">{detailsError}</Alert>
                    ) : null}

                    {!detailsLoading && !detailsError && detailsData.length > 0 ? (
                        <Paper variant="outlined" sx={{ borderRadius: 1.5, overflow: "hidden" }}>
                            <TableContainer sx={{ maxHeight: 420 }}>
                                <Table stickyHeader size="small">
                                    <TableHead>
                                        <TableRow>
                                            {getModalColumns(detailsData).map((col) => (
                                                <TableCell
                                                    key={col.id}
                                                    sx={{
                                                        fontWeight: "bold",
                                                        backgroundColor: "#f4ebf8",
                                                        color: "#632584",
                                                        fontSize: "0.8rem",
                                                        py: 1,
                                                        whiteSpace: "nowrap",
                                                    }}
                                                >
                                                    {col.label}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {detailsData.map((row, rIdx) => {
                                            const cols = getModalColumns(detailsData);
                                            return (
                                                <TableRow
                                                    key={rIdx}
                                                    sx={{
                                                        "&:hover": { backgroundColor: "#faf5fc" },
                                                        transition: "background-color 0.15s ease",
                                                    }}
                                                >
                                                    {cols.map((col) => {
                                                        const rawVal = typeof row === "object" && row !== null ? row[col.id] : row;
                                                        return (
                                                            <TableCell key={col.id} sx={{ fontSize: "0.8rem", py: 0.85, whiteSpace: "nowrap" }}>
                                                                {formatTableCellValue(rawVal)}
                                                            </TableCell>
                                                        );
                                                    })}
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    ) : null}

                    {!detailsLoading && !detailsError && detailsData.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                            No records found for the selected date.
                        </Typography>
                    ) : null}
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 1.5 }}>
                    <Button onClick={handleCloseDetailsModal} variant="contained" color="secondary" size="small" sx={{ textTransform: "none", borderRadius: 1.5 }}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </UIWrapper>
    );
};

export default NateraDashboard;